import React from 'react';
import InputField from '../../../components/inputs/inputField';
import TextAreaField from '../../../components/inputs/textAreaField';
import SelectField from '../../../components/inputs/selectField';
import ToggleField from '../../../components/inputs/toggleField';
import EventSessionPolls from './polls';
import EventSessionModerators from './moderators';
import AddVirtualLinkModal from '../../../views/account/modals/addVirtualLinkModal';
import API from '../../../utils/API';

class App extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			addVirtualLinkModalOpen: false,
			virtualLinkModalContext: null, // 'livestream' | { type: 'backup', index: number }
			localVirtualLinks: (props.organization && props.organization.virtualLinks)
				? props.organization.virtualLinks
				: [],
			kollectivePublishing: false,
		};
	}

	componentDidUpdate(prevProps) {
		// Sync if organization.virtualLinks changes externally
		if (
			prevProps.organization !== this.props.organization &&
			this.props.organization &&
			this.props.organization.virtualLinks
		) {
			this.setState({ localVirtualLinks: this.props.organization.virtualLinks });
		}
	}

	toggleAddVirtualLinkModal = (context = null) => {
		this.setState({
			addVirtualLinkModalOpen: !this.state.addVirtualLinkModalOpen,
			virtualLinkModalContext: context
		});
	};

	handleAddVirtualLink = (linkData) => {
		const { orgId, updateSessionField, editedSession } = this.props;
		const { virtualLinkModalContext } = this.state;

		const newLink = {};
		if (linkData.linkType === 'custom') {
			newLink.name = linkData.name;
			newLink.url = linkData.url;
		} else if (linkData.linkType === 'aws') {
			newLink.channelName = linkData.name;
		}

		API().post(`/Organizations/${orgId}/virtualLink`, {
			request: 'create',
			...newLink
		}).then((res) => {
			if (res.data) {
				this.setState(
					(prevState) => ({
						localVirtualLinks: [...prevState.localVirtualLinks, res.data],
						addVirtualLinkModalOpen: false,
						virtualLinkModalContext: null
					}),
					() => {
						if (res.data.url) {
							if (virtualLinkModalContext === 'livestream') {
								// Assign to the main livestream link
								updateSessionField('virtualMeetingLink', res.data.url);
							} else if (
								virtualLinkModalContext &&
								virtualLinkModalContext.type === 'backup'
							) {
								// Assign to the specific backup link at the stored index
								const backuplist = JSON.parse(
									JSON.stringify(editedSession.backupVirtualMeetingLinks || [])
								);
								backuplist[virtualLinkModalContext.index] = res.data.url;
								updateSessionField('backupVirtualMeetingLinks', backuplist);
							}
						}
					}
				);
			} else {
				this.setState({ addVirtualLinkModalOpen: false, virtualLinkModalContext: null });
			}
		});
	};

	// ── Kollective ECDN ───────────────────────────────────────────────────────
	// Publishing is a direct API action — not part of the normal form save flow.
	// After publish/unpublish the local editedSession is updated via updateSessionField
	// so the UI immediately reflects the new state without requiring a page reload.

	publishKollectiveContent = async () => {
		const { orgId, event, editedSession, updateSessionField } = this.props;
		this.setState({ kollectivePublishing: true });
		try {
			const res = await API().post(
				`Organizations/${orgId}/events/${event._id}/sessions/${editedSession._id}/integrations/kollective/publishContent`
			);
			if (res.data && res.data.success) {
				// Store the content ID (for status display) and token (for viewer pages).
				updateSessionField('kollectiveContentId', res.data.contentId);
				updateSessionField('kollectiveContentToken', res.data.contentToken);
			}
		} catch (e) {
			console.error('[Kollective] publish error', e);
		}
		this.setState({ kollectivePublishing: false });
	};

	unpublishKollectiveContent = async () => {
		const { orgId, event, editedSession, updateSessionField } = this.props;
		this.setState({ kollectivePublishing: true });
		try {
			await API().delete(
				`Organizations/${orgId}/events/${event._id}/sessions/${editedSession._id}/integrations/kollective/publishContent`
			);
			updateSessionField('kollectiveContentId', '');
			updateSessionField('kollectiveContentToken', '');
		} catch (e) {
			console.error('[Kollective] unpublish error', e);
		}
		this.setState({ kollectivePublishing: false });
	};

	render() {
		const { editedSession, updateSessionField, readOnly } = this.props;
		const { virtualMeetingLink, recordingLink, pageOptions, pageId } = editedSession;
		const { addVirtualLinkModalOpen, localVirtualLinks, kollectivePublishing } = this.state;
		const isWebinar = editedSession.virtualMeetingType === 'webinar';
		const selectedPage = pageOptions.find(
			(po) => po._id.toString() == pageId && pageId.toString()
		) || { title: '', _id: null };

		// Build livestream options from local virtual links (includes newly created ones)
		const virtualLinkOptions = localVirtualLinks.map(link => ({
			label: link.name,
			value: link.url
		}));

		// Add empty option at the beginning
		const livestreamOptions = [{ label: 'Select a link...', value: '' }, ...virtualLinkOptions];

		// Kollective: show the section only when the event has the integration enabled
		const kollectiveEventEnabled = !!(
			this.props.event &&
			this.props.event.integrations &&
			this.props.event.integrations.kollective &&
			this.props.event.integrations.kollective.enabled
		);

		return (
			<div className='sectionBody posRel  p-0 mt-20'>
				{isWebinar ? (
					<InputField
						label={'Livestream Link'}
						value={livestreamOptions.find(opt => opt.value === virtualMeetingLink)?.label || virtualMeetingLink || ''}
						disabled={true}
						type="text"
						inputStyle={{ overflow: 'hidden !important', textOverflow: 'unset !important', color: 'var(--outline-border)', paddingRight: '30px' }}
						inputIcon={<i className="las la-lock" />}
					/>
				) : (
					<div>
						<div className="labelContainer">
							<div className="flex jcsb ">
								<div className="formFieldLabel">Livestream Link</div>
								{!readOnly && (
									<div
										className="se-blue c-pointer "
										style={{ marginBottom: 8 }}
										onClick={() => this.toggleAddVirtualLinkModal('livestream')}
									>
										Create Virtual Link
									</div>
								)}
							</div>

						</div>
						<SelectField
							value={livestreamOptions.find(opt => opt.value === virtualMeetingLink) || livestreamOptions[0]}
							options={livestreamOptions}
							disabled={readOnly}
							onChange={(selected) => updateSessionField('virtualMeetingLink', selected ? selected.value : '')}
						/>
					</div>
				)}

				<InputField
					label={'Recording Link'}
					value={recordingLink}
					disabled={readOnly}
					type="text"
					onChange={(e) => updateSessionField('recordingLink', e.target.value)}
				/>

				<label className="displayLabe mb-10">
					<div className="flex-ac ">
						<div
							onClick={() => {
								const currentLinks = editedSession.backupVirtualMeetingLinks || [];
								updateSessionField('backupVirtualMeetingLinks', [...currentLinks, '']);
							}}
						>
							<i className="las la-plus-circle mr-10 c-pointer clickable" />
						</div>
						Add Back-Up Session Links
					</div>
				</label>
				{editedSession.backupVirtualMeetingLinks && editedSession.backupVirtualMeetingLinks.map((link, index) => {
					return (
						<div key={index} className="flex-sb w-100 aic" style={{ gap: '10px' }}>
							<div className="w-100">
								<div className="labelContainer">
									<div className="flex jcsb ">
										<div className="formFieldLabel">Back up Session Link</div>
										{!readOnly && (
											<div
												className="se-blue c-pointer"
												style={{ marginBottom: 8 }}
												onClick={() => this.toggleAddVirtualLinkModal({ type: 'backup', index })}
											>
												Create Virtual Link
											</div>
										)}
									</div>

								</div>
								<SelectField
									value={livestreamOptions.find(opt => opt.value === editedSession.backupVirtualMeetingLinks[index]) || livestreamOptions[0]}
									options={livestreamOptions}
									classes="w-100"
									onChange={(selected) => {
										let backuplist = JSON.parse(JSON.stringify(editedSession.backupVirtualMeetingLinks));
										backuplist[index] = selected ? selected.value : '';
										updateSessionField('backupVirtualMeetingLinks', backuplist);
									}}
								/>
							</div>
							<div
								onClick={() => {
									editedSession.backupVirtualMeetingLinks.splice(index, 1);
									updateSessionField('backupVirtualMeetingLinks', editedSession.backupVirtualMeetingLinks);
								}}
								className="flex aic jcc"
								style={{ marginTop: '10px', cursor: 'pointer', minWidth: '24px' }}
							>
								<i className="las la-trash" style={{ color: 'red' }} />
							</div>
						</div>
					);
				})}

				{/* ── Kollective ECDN ──────────────────────────────────────────── */}
				{kollectiveEventEnabled && (
					<div className='mt-20'>
						<h4 className='blockHeader'>Kollective ECDN</h4>
						<p className='small gray' style={{ margin: '0 0 10px' }}>
							Publish this session to Kollective so viewers receive the stream via edge-CDN delivery.
						</p>
						{editedSession.kollectiveContentId ? (
							<div>
								<p className='small' style={{ color: '#27ae60', marginBottom: 8 }}>
									<i className='las la-check-circle mr-5' />
									Published — Content ID: {editedSession.kollectiveContentId}
								</p>
								{!readOnly && (
									<button
										className='neu'
										disabled={kollectivePublishing}
										onClick={this.unpublishKollectiveContent}
									>
										{kollectivePublishing
											? <><i className='las la-spinner la-spin mr-5' />Removing…</>
											: <><i className='las la-times mr-5' />Remove from Kollective</>}
									</button>
								)}
							</div>
						) : (
							<>
								{!readOnly && (
									<button
										className='prim'
										disabled={kollectivePublishing || !editedSession.virtualMeetingLink}
										onClick={this.publishKollectiveContent}
									>
										{kollectivePublishing
											? <><i className='las la-spinner la-spin mr-5' />Publishing…</>
											: <><i className='las la-broadcast-tower mr-5' />Publish to Kollective</>}
									</button>
								)}
								{!editedSession.virtualMeetingLink && (
									<p className='small' style={{ color: '#e67e22', marginTop: 6 }}>
										A livestream link is required before publishing to Kollective.
									</p>
								)}
							</>
						)}
					</div>
				)}

				<div className="flex-sb w-100" style={{ marginTop: 20 }}>
					<InputField
						label={'Start Offset'}
						value={editedSession.virtualStartOffset}
						disabled={readOnly}
						type="text"
						classes="mr-10 w-100"
						onChange={(e) => updateSessionField('virtualStartOffset', e.target.value)}
					/>

					<InputField
						label={'Stop Offset'}
						value={editedSession.virtualStopOffset}
						disabled={readOnly}
						type="text"
						classes="ml-10 w-100"
						onChange={(e) => updateSessionField('virtualStopOffset', e.target.value)}
					/>
				</div>

				<h4 className='blockHeader'>Features</h4>
				<div>
					<ToggleField
						label="Enable Chat"
						checked={editedSession.chatEnabled}
						icons={false}
						disabled={readOnly}
						onChange={(e) => updateSessionField('chatEnabled', !editedSession.chatEnabled)}
					/>

					<ToggleField
						label={'Enable Stream Reactions'}
						checked={editedSession.reactionsEnabled}
						icons={false}
						disabled={readOnly}
						onChange={(e) =>
							updateSessionField('reactionsEnabled', !editedSession.reactionsEnabled)}
					/>

					<ToggleField
						label="Enable Polls"
						checked={editedSession.pollsEnabled}
						icons={false}
						disabled={readOnly}
						onChange={(e) => updateSessionField('pollsEnabled', !editedSession.pollsEnabled)}
					/>

					<ToggleField
						label="Enable Realtime 3D Effects"
						checked={editedSession.enable3dOverlays}
						icons={false}
						disabled={readOnly}
						onChange={(e) =>
							updateSessionField('enable3dOverlays', !editedSession.enable3dOverlays)}
					/>
					{editedSession.enable3dOverlays && (
						<ToggleField
							label="Fireworks"
							checked={editedSession.available3dOverlays.includes('Fireworks')}
							icons={false}
							disabled={readOnly}
							onChange={(e) => {
								const available3dOverlays = editedSession.available3dOverlays.includes(
									'Fireworks'
								)
									? editedSession.available3dOverlays.filter((o) => o != 'Fireworks')
									: [...editedSession.available3dOverlays, 'Fireworks'];
								updateSessionField('available3dOverlays', available3dOverlays);
							}}
						/>
					)}
					{editedSession.enable3dOverlays && (
						<ToggleField
							label={'Snowfall'}
							checked={editedSession.available3dOverlays.includes('Snow')}
							icons={false}
							disabled={readOnly}
							onChange={(e) => {
								const available3dOverlays = editedSession.available3dOverlays.includes(
									'Snow'
								)
									? editedSession.available3dOverlays.filter((o) => o != 'Snow')
									: [...editedSession.available3dOverlays, 'Snow'];
								updateSessionField('available3dOverlays', available3dOverlays);
							}}
						/>
					)}
					{editedSession.enable3dOverlays && (
						<ToggleField
							label={'Money Drop'}
							checked={editedSession.available3dOverlays.includes('Money')}
							icons={false}
							disabled={readOnly}
							onChange={(e) => {
								const available3dOverlays = editedSession.available3dOverlays.includes(
									'Money'
								)
									? editedSession.available3dOverlays.filter((o) => o != 'Money')
									: [...editedSession.available3dOverlays, 'Money'];
								updateSessionField('available3dOverlays', available3dOverlays);
							}}
						/>
					)}
				</div>

				<h4 className='blockHeader  mt-0'>Polls</h4>
				<div>
					<EventSessionPolls
						editedSession={this.props.editedSession}
						event={this.props.event}
						orgId={this.props.orgId}
						readOnly={readOnly}
						loadMoreAttendees={this.props.loadMoreAttendees}
						updateSession={this.updateSession}
						updateSpeakers={this.props.updateSpeakers}
					/>
				</div>
				<h4 className='blockHeader'>Moderators</h4>
				<div>
					<EventSessionModerators
						editedSession={this.props.editedSession}
						event={this.props.event}
						orgId={this.props.orgId}
						readOnly={readOnly}
						loadMoreAttendees={this.props.loadMoreAttendees}
						updateSession={this.updateSession}
						updateModerators={this.props.updateModerators}
						updatePolls={this.updatePolls}
						resetPoll={this.resetPoll}
					/>
				</div>

				{addVirtualLinkModalOpen && (
					<AddVirtualLinkModal
						isOpen={addVirtualLinkModalOpen}
						toggle={this.toggleAddVirtualLinkModal}
						onSave={this.handleAddVirtualLink}
						mode="add"
						existingLinks={localVirtualLinks}
					/>
				)}
			</div>
		);
	}
}

export default App;
